import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import KnightLogo from "../components/site/KnightLogo";
import { ArrowRight, Lock } from "lucide-react";

export default function AgentLogin() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/agent" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/agent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FA] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-10">
          <KnightLogo className="h-14 w-auto" tone="dark" />
        </Link>
        <div className="rounded-3xl bg-white border border-black/5 shadow-sm p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#011233]/5 text-[#011233] flex items-center justify-center">
              <Lock size={16} />
            </div>
            <div>
              <h1 className="text-2xl font-light tracking-tight text-slate-900">Agent Portal</h1>
              <p className="text-sm text-slate-500">Sign in to view leads & messages</p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                Email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@knightinssolutions.com"
                className="kn-input"
                data-testid="login-email"
              />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                Password
              </span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="kn-input"
                data-testid="login-password"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="kn-btn-primary inline-flex items-center justify-center gap-2 w-full rounded-full px-6 py-3.5 text-base font-medium disabled:opacity-60"
              data-testid="login-submit"
            >
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <ArrowRight size={18} />}
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-500 mt-6">
          <Link to="/" className="hover:text-[#011233] transition-colors">← Back to site</Link>
        </p>
      </div>
      <style>{`
        .kn-input {
          width: 100%;
          background: #F7F9FA;
          border: 1px solid rgba(15,23,42,0.06);
          border-radius: 14px;
          padding: 14px 16px;
          font-size: 15px;
          color: #011233;
          transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
          font-family: inherit;
        }
        .kn-input::placeholder { color: #94A3B8; }
        .kn-input:focus {
          outline: none;
          border-color: #011233;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(17,42,52,0.06);
        }
      `}</style>
    </div>
  );
}
